import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, Accordion, AccordionSummary, AccordionDetails, Typography, Alert, List, ListItem, ListItemText } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { makeStyles } from "tss-react/mui";
import DropUpload from "components/Elements/DropUploadBox";
import { BASE_API_PATH, UPLOAD_CSV_NOTE } from "helpers/constants";
import { importSiteAssets } from "services/clients/sites/siteAssets";
import ImportTable from "../components/ImportTable";
import AddDialogStyle from "styles/application/AddDialogStyle";
import { DownloadCSVTemplateForSiteSetting } from "services/services/serviceLists";
import { getLocalStorageData } from "helpers/utils";

const AT = AddDialogStyle();

const media = "@media (max-width:414px)";

const defaultData = {
	newReferences: [],
	newAssets: [],
	modifiedReferences: [],
	modifiedAssets: [],
};

const useStyles = makeStyles()((theme) => ({
	content: {
		display: "flex",
		flexDirection: "column",
		gap: 5,
		marginBottom: 15,
	},
	button: {
		marginTop: 15,
		width: "30%",
		[media]: {
			width: "auto",
		},
	},
	errorMessage: {
		color: "#f44336",
		fontWeight: "normal",
		fontSize: "1.25rem",
		margin: "10px 0",
	},
}));

const ImportDialog = ({
	open,
	closeHandler,
	siteId,
	onImportSuccess,
	getError,
	showAssetReferences = false,
}) => {
	const { classes } = useStyles();
	const [loading, setLoading] = useState(false);
	const [file, setFile] = useState({});
	const [data, setData] = useState(defaultData);
	const [show, setShow] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [importErrors, setImportErrors] = useState(null);
	const { customCaptions } = getLocalStorageData("me");

	const closeOverride = () => {
		setFile({});
		closeHandler();
		setData(defaultData);
		setShow(false);
		setLoading(false);
		setErrorMessage("");
		setImportErrors(null);
	};

	const downloadImportCSVTemplate = async () => {
		try {
			const response = await DownloadCSVTemplateForSiteSetting();
			if (response.status && response.data) {
				// Create and trigger download immediately
				const element = document.createElement("a");
				const file = new Blob([response.data], { type: 'text/csv' });
				element.href = URL.createObjectURL(file);
				element.download = "asset-register-import-template.csv";
				document.body.appendChild(element);
				element.click();
				document.body.removeChild(element);
				URL.revokeObjectURL(element.href);
			}
		} catch (err) {
			console.warn("Failed to download CSV template:", err);
		}
	};

	const importDocument = async (key, shouldImport) => {
		try {
			const response = await importSiteAssets(siteId, {
				key,
				import: shouldImport,
			});

			if (response?.status) {
				setErrorMessage("");
				setImportErrors(null);
				return response;
			} else {
				// Handle 400 response with errors array
				const responseData = response?.data;
				
				if (responseData?.errors && Array.isArray(responseData.errors)) {
					// New simple error array format
					setImportErrors(responseData.errors);
					setErrorMessage("Upload failed due to validation errors");
				} else {
					// Fallback to old error handling
					const errorMsg = response?.data?.detail || response?.data || "Failed to import";
					getError(errorMsg);
					setErrorMessage(typeof errorMsg === 'string' ? errorMsg : "Failed to import");
				}
				
				return {
					status: false,
					messageType: "Danger",
					data: responseData?.preview || defaultData,
				};
			}
		} catch (err) {
			// Handle errors in catch block including 400 responses
			const responseData = err?.data;
			
			if (responseData?.errors && Array.isArray(responseData.errors)) {
				setImportErrors(responseData.errors);
				setErrorMessage("Upload failed due to validation errors");
			} else {
				const errorMsg = err?.data?.detail || err?.data || "Failed to import";
				getError(errorMsg);
				setErrorMessage(typeof errorMsg === 'string' ? errorMsg : "Failed to import");
			}
			return {
				status: false,
				messageType: "Danger",
				data: defaultData,
			};
		}
	};

    const onDocumentUpload = async (key, url) => {
        setLoading(true);
        setFile({ key, url });

        try {
            const response = await importDocument(key, false);
            setShow(true);
            setData(response.data || defaultData);
        } catch (err) {
            setErrorMessage("Upload failed");
            setData(defaultData);
        } finally {
            setLoading(false);
        }
    };

    const onImportAsset = async () => {
        setLoading(true);
        
        try {
            const response = await importDocument(file.key, true);
            if (!errorMessage && response.status) {
                await onImportSuccess();
                closeOverride();
            }
        } catch (err) {
        } finally {
            setLoading(false);
        }
    };

	const { newAssets = [], newReferences = [], modifiedReferences = [], modifiedAssets = [] } = data;
	
	const hasChanges = 
		newAssets.length > 0 ||
		newReferences.length > 0 ||
		modifiedReferences.length > 0 ||
		modifiedAssets.length > 0;


	return (
		<Dialog open={open} onClose={closeOverride} fullWidth maxWidth="md">
			<AT.ActionContainerFileUpload>
				<DialogTitle>
					<AT.HeaderText>Import {customCaptions?.assetPlural || "Assets"}</AT.HeaderText>
					<AT.NoteText>{UPLOAD_CSV_NOTE}</AT.NoteText>
				</DialogTitle>
				<AT.NoteButtonContainer>
					<AT.CancelButton variant="contained" onClick={closeOverride}>
						Cancel
					</AT.CancelButton>
				</AT.NoteButtonContainer>
			</AT.ActionContainerFileUpload>

			<DialogContent>
				<div className={classes.content}>
                {importErrors && (
                    <Alert severity="error" style={{ marginBottom: "16px" }}>
                        <Typography variant="h6" gutterBottom>
                            Import {customCaptions?.assetPlural || "Assets"} Failed
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                            The following validation errors were found:
                        </Typography>
                        <List dense>
                            {importErrors.map((error, index) => (
                                <ListItem key={index} style={{ paddingLeft: 0, paddingTop: 0, paddingBottom: 0 }}>
                                    <ListItemText 
                                        primary={error}
                                        primaryTypographyProps={{ variant: "body2" }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Alert>
                )}

                {!show && (
                    <DropUpload
                        filesUploading={loading}
                        setFilesUploading={setLoading}
                        isImageUploaded={false}
                        uploadReturn={onDocumentUpload}
                        apiPath={`${BASE_API_PATH}SiteAssets/${siteId}/uploadList`}
                    />
                )}

                {show && hasChanges && (
                    <Accordion defaultExpanded={!importErrors} style={{ marginTop: "16px" }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle1">Import Preview - {showAssetReferences ? (newReferences.length + modifiedReferences.length) : (newAssets.length + modifiedAssets.length)} items will be imported</Typography>
                        </AccordionSummary>
                        <AccordionDetails style={{ padding: "8px 16px" }}>
                            {showAssetReferences ? (
									<>
										<ImportTable title={`New ${customCaptions?.assetReferencePlural || "References"}`} data={newReferences} isReferenceMode={true} customCaptions={customCaptions} />
										<ImportTable title={`Modified ${customCaptions?.assetReferencePlural || "References"}`} data={modifiedReferences} isReferenceMode={true} customCaptions={customCaptions} />
									</>
								) : (
									<>
										<ImportTable title={`New ${customCaptions?.assetPlural || "Assets"}`} data={newAssets} isReferenceMode={false} customCaptions={customCaptions} />
										<ImportTable title={`Modified ${customCaptions?.assetPlural || "Assets"}`} data={modifiedAssets} isReferenceMode={false} customCaptions={customCaptions} />
									</>
								)}
							</AccordionDetails>
						</Accordion>
					)}

                {show && !hasChanges && !importErrors && (
                    <h1>No changes will be made</h1>
                )}
				</div>

				<div style={{ height: "20px" }} />
				
				{/* Bottom action buttons */}
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<span 
						style={{ cursor: "pointer", fontSize: "16px", color: "#307AD6", textDecoration: "underline" }}
						onClick={downloadImportCSVTemplate}
					>
						Download Import CSV Template
					</span>
					
					{show && hasChanges && (
						<AT.ConfirmButton 
							variant="contained"
							onClick={onImportAsset}
							disabled={loading}
						>
							{loading ? "Importing..." : "Import"}
						</AT.ConfirmButton>
					)}
				</div>
				
				<div style={{ height: "10px" }} />
			</DialogContent>
		</Dialog>
	);
};

export default ImportDialog;
